import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  Truck,
  MapPin,
  Clock,
  Leaf,
  Database,
  ShieldCheck,
  Building2,
  Calendar,
  ChevronDown,
} from "lucide-react";
import { useLang } from "@/lib/i18n";
import {
  useSEO,
  breadcrumbJsonLd,
  serviceJsonLd,
  faqPageJsonLd,
  siteUrl,
} from "@/lib/seo";
import CTASection from "@/components/CTASection";

const PAGE_FAQS = [
  {
    q_nl: "Hoe werkt mobiele truckwash?",
    a_nl: "Onze service-units rijden naar uw locatie en reinigen daar uw vrachtwagens, trekkers, opleggers en bedrijfsvoertuigen. Wij brengen alles mee: water, biologisch afbreekbare middelen, hogedrukapparatuur en gecontroleerde waterafvoer. U levert alleen de standplaats; wij doen de rest.",
  },
  {
    q_nl: "Wassen jullie op locatie?",
    a_nl: "Ja, Wasz.nl is een 100% mobiele truckwash. Wij hebben geen vaste wasstraat. Reiniging vindt plaats op uw bedrijfsterrein, op een distributiecentrum, of op een afgesproken parkeerlocatie in heel Nederland.",
  },
  {
    q_nl: "Welke voertuigen reinigen jullie?",
    a_nl: "Wij reinigen vrachtwagens, trekkers, opleggers, trailers, bakwagens, bedrijfsbussen en distributievoertuigen. Voor wagenparkbeheer leveren wij contractdiensten voor vloten vanaf 10 voertuigen.",
  },
  {
    q_nl: "Werkt Wasz.nl buiten Almere?",
    a_nl: "Ja. Vanuit Almere bedienen wij heel Nederland. Onze mobiele teams werken in onder andere Amsterdam, Den Haag, Rotterdam, Utrecht en alle bijbehorende industrieterreinen en havengebieden.",
  },
  {
    q_nl: "Hoe vaak moet een vrachtwagen gewassen worden?",
    a_nl: "Dat hangt af van uw inzet en sector. Voor een representatieve vloot is wekelijks of tweewekelijks gangbaar. Voor voedseltransport vragen klanten en NVWA-toezicht doorgaans om een vast reinigingsritme inclusief HACCP-certificaat per wasbeurt.",
  },
  {
    q_nl: "Krijg ik per voertuig een digitale reinigingsregistratie?",
    a_nl: "Ja. Elke wasbeurt wordt automatisch geregistreerd in ons klantportaal WaszBase, gekoppeld aan het kenteken. U ziet datum, locatie, operator, type werkwijze en — voor HACCP-werkwijze — contact- en droogtijd. Per kenteken bouwt zich een digitaal dossier op dat u op elk moment kunt exporteren als PDF.",
  },
  {
    q_nl: "Hoe ondersteunt Wasz.nl mijn HACCP- en auditverplichtingen?",
    a_nl: "Voor levensmiddelentransport voeren wij HACCP-conforme interieurreiniging uit met digitaal certificaat per kenteken in WaszBase. Voor leveranciersaudits en NVWA-controles kunt u binnen minuten een compleet dossier exporteren — gefilterd op kenteken, locatie of periode. Wij denken proactief mee in de voorbereiding op een audit.",
  },
  {
    q_nl: "Werkt mobiele truckwash ook voor multi-site wagenparken?",
    a_nl: "Ja. Voor wagenparken met meerdere vestigingen werken wij met geconsolideerde contracten: één planner, één digitaal dossier in WaszBase en sub-klantfacturatie per locatie. Voor uw boekhouding ontvangt u maandelijks één factuur met kostenuitsplitsing per vestiging.",
  },
];

const MobieleTruckwashSEO = () => {
  const { lang } = useLang();
  const canonical = siteUrl("/mobiele-truckwash");

  useSEO({
    title: "Mobiele Truckwash op Locatie — Heel NL | Wasz.nl",
    description:
      "Mobiele truckwash op uw locatie in heel Nederland — ★ 5.0 op Google ✓ HACCP & WaszBase inbegrepen. Offerte binnen 24 uur.",
    canonical,
    image: siteUrl("/og-image-ai.jpg?v=4"),
    lang,
    jsonLd: [
      {
        id: "mobile-truckwash-service-jsonld",
        data: serviceJsonLd({
          name: "Mobiele Truckwash op Locatie",
          description:
            "Mobiele vrachtwagenreiniging op locatie voor transportbedrijven en wagenparken in heel Nederland.",
          url: canonical,
        }),
      },
      {
        id: "mobile-truckwash-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: "Mobiele Truckwash op Locatie", url: canonical },
        ]),
      },
      {
        id: "mobile-truckwash-faq",
        data: faqPageJsonLd(PAGE_FAQS, "nl"),
      },
    ],
  });

  const benefits = [
    {
      icon: Clock,
      title: "Geen stilstand voor uw vloot",
      body:
        "Geen rit naar een wasstraat, geen wachttijd, geen verloren chauffeursuren. Wij wassen wanneer en waar de vrachtwagen toch al staat — vaak buiten de piekuren.",
    },
    {
      icon: MapPin,
      title: "Op uw eigen terrein",
      body:
        "Onze service-units reinigen op uw bedrijfslocatie, distributiecentrum of laad-/losplaats. Eén afspraak, ongestoorde operatie.",
    },
    {
      icon: Leaf,
      title: "Milieuvriendelijk reinigen",
      body:
        "Biologisch afbreekbare reinigingsmiddelen, beheerst waterverbruik en gecontroleerde afvoer conform de Nederlandse milieuregelgeving.",
    },
    {
      icon: Database,
      title: "Digitaal dossier via WaszBase",
      body:
        "Elke wasbeurt wordt vastgelegd per kenteken in WaszBase. Reinigingscertificaten en facturen zijn één klik weg, geschikt voor NVWA-controles.",
    },
    {
      icon: ShieldCheck,
      title: "Buitenkant standaard, binnenkant op maat",
      body:
        "Bij elke wasbeurt reinigen wij standaard de buitenkant. De laadruimte doen wij erbij op verzoek — vrijblijvend, of HACCP-conform met digitaal certificaat voor klanten met NVWA-controles. Komen wij alléén voor de binnenkant omdat uw eigen wasstraat geen tijd heeft? Ook dat plannen wij in.",
    },
    {
      icon: Truck,
      title: "Geschikt voor elk voertuig",
      body:
        "Trekkers, opleggers, trailers, bakwagens, koeltrucks, distributievoertuigen, terminal-trekkers en bedrijfsbussen — wij hebben de protocollen klaar.",
    },
  ];

  const regions = [
    { city: "Almere", slug: "almere", note: "Postadres & operationele basis" },
    { city: "Amsterdam", slug: "amsterdam", note: "Westpoort, Schiphol-corridor" },
    { city: "Den Haag", slug: "den-haag", note: "Forepark, Binckhorst" },
    { city: "Rotterdam", slug: "rotterdam", note: "Maasvlakte, Europoort, Eemhaven" },
    { city: "Utrecht", slug: "utrecht", note: "Lage Weide, Oudenrijn, Houten" },
  ];

  const Section = ({ id, overline, title, children }) => (
    <section id={id} className="scroll-mt-28 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {overline && <p className="section-overline mb-3">{overline}</p>}
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-6">
          {title}
        </h2>
        <div className="text-white/70 text-base sm:text-[17px] leading-relaxed space-y-5 max-w-3xl">
          {children}
        </div>
      </div>
    </section>
  );

  return (
    <main data-testid="mobiele-truckwash-seo-page" className="min-h-screen pt-20 pb-10">
      {/* === HERO === */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a18] via-[#050509] to-[#050509] -z-10" />
        <div className="mx-auto max-w-6xl px-6 lg:px-8 pt-12 pb-20 sm:pt-16">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/45 flex items-center gap-2">
            <Link to="/" className="hover:text-[#00D4FF]">Home</Link>
            <span>›</span>
            <span className="text-white/70">Mobiele Truckwash op Locatie</span>
          </nav>
          <p className="section-overline mb-3 inline-flex items-center gap-2">
            <Truck className="w-3.5 h-3.5" /> Mobiele truckwash
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02] max-w-4xl">
            Mobiele Truckwash <span className="text-[#00D4FF]">op Locatie</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg sm:text-xl leading-relaxed max-w-3xl">
            Wasz.nl is een 100% mobiele truckwash voor transportbedrijven en wagenparken in heel Nederland. Wij reinigen vrachtwagens op uw eigen locatie — geen stilstand, geen omrijden, volledig HACCP-conform, met digitale registratie via WaszBase.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact?type=quote" data-testid="seo-cta-quote">
              <button className="btn-primary">
                Vraag een offerte aan <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/waszbase" data-testid="seo-cta-waszbase">
              <button className="btn-secondary">
                Bekijk WaszBase
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* === INTRO TEXT === */}
      <Section
        id="wat-is"
        overline="Wat is mobiele truckwash?"
        title="Professionele vrachtwagenreiniging op locatie — overal in Nederland."
      >
        <p>
          <strong className="text-white">Mobiele truckwash</strong> betekent dat wij met volledig uitgeruste service-units naar uw locatie komen om vrachtwagens, trekkers, trailers en bedrijfsvoertuigen ter plaatse te reinigen. Uw wagenpark hoeft niet stil te staan en hoeft niet om te rijden naar een vaste wasstraat. De wasbeurt past gewoon in uw operationele ritme: tussen aankomst en de volgende rit in, of buiten de piekuren als dat beter uitkomt.
        </p>
        <p>
          Voor transportondernemers in Nederland is dit een logische manier om <strong className="text-white">vrachtwagens te reinigen</strong>: u verliest geen rijtijd, geen brandstof aan omrijden en geen chauffeursuren aan wachttijden bij externe wasstraten. Onze service is opgezet voor structurele contracten — van wagenparken vanaf 10 voertuigen tot vloten van honderden voertuigen.
        </p>
        <p>
          Wasz.nl bestaat sinds 2016 en is opgebouwd door professionals met meer dan 25 jaar ervaring in vrachtwagenreiniging. Vanuit <Link to="/truckwash-almere" className="text-[#00D4FF] hover:underline">Almere</Link> bedienen wij heel transport-Nederland met dezelfde digitale ondersteuning via het klantportaal <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link>. Op locatie werken wij dagelijks voor wagenparken in onder andere <Link to="/truckwash-amsterdam" className="text-[#00D4FF] hover:underline">Amsterdam</Link>, <Link to="/truckwash-utrecht" className="text-[#00D4FF] hover:underline">Utrecht</Link>, Rotterdam en Den Haag.
        </p>
      </Section>

      {/* === SCOPE: wat reinigen wij precies === */}
      <Section
        id="onze-scope"
        overline="Onze reinigingsscope"
        title="Buitenkant of binnenkant — los of gecombineerd."
      >
        <p>
          Elke vloot is anders. De ene transporteur wil alleen een representatieve buitenkant, de andere heeft elke wasbeurt een HACCP-certificaat voor de laadruimte nodig, en weer een ander wil dat we juist alléén de binnenkant doen omdat de eigen wasstraat geen tijd heeft. Wij plannen de scope die u nodig heeft — en niets meer.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 not-prose mt-6">
          {[
            {
              tag: "Standaard",
              title: "Buitenkant reiniging",
              body:
                "Cabine, opbouw, velgen, ramen, spiegels en op verzoek het chassis. Onze standaarddienst — beschikbaar voor elke klant, elk voertuig.",
            },
            {
              tag: "Op verzoek",
              title: "Binnenkant laadruimte — zonder certificaat",
              body:
                "Voor klanten die de laadruimte schoon willen tussen ladingen, zonder dat er een NVWA-eis ligt. Geen certificaat, gewoon een schone vloer en wanden.",
            },
            {
              tag: "Voor foodservice",
              title: "Binnenkant HACCP-conform met certificaat",
              body:
                "Voor levensmiddelentransport: laadruimte gereinigd volgens HACCP-protocol, met digitaal certificaat per kenteken in WaszBase — klaar voor NVWA-controles en audits.",
            },
            {
              tag: "Overflow-werk",
              title: "Alleen de binnenkant — uitbesteed",
              body:
                "Heeft uw eigen wasstraat geen capaciteit voor de laadruimte? Wij komen op locatie om alléén de binnenkant te doen, terwijl de buitenkant elders gebeurt.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-white/10 bg-[#0F0F23] p-6"
              data-testid={`scope-card-${item.tag.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-[#00D4FF] mb-2.5">
                {item.tag}
              </span>
              <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6">
          Geen verplichte combinaties, geen vaste pakketten waar u betaalt voor wat u niet gebruikt. U vertelt wat er moet gebeuren, wij voeren het uit op locatie en leggen het vast in <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link>.
        </p>
      </Section>

      {/* === BENEFITS GRID === */}
      <section id="voordelen" className="scroll-mt-28 py-16 sm:py-20 bg-[#0a0a18]/40 border-y border-white/5">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <p className="section-overline mb-3">Voordelen</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-10">
            Waarom transportbedrijven kiezen voor mobiele truckwash.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-white/10 bg-[#0F0F23] p-6 hover:border-[#00D4FF]/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
                </div>
                <h3 className="text-base font-semibold text-white tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === TIME SAVINGS === */}
      <Section
        id="tijdsbesparing"
        overline="Tijdwinst"
        title="Mobiele truckwash bespaart uw wagenpark uren per week."
      >
        <p>
          Een gemiddelde rit naar een vaste wasstraat in de regio kost al snel 35 tot 50 minuten rijtijd, exclusief wachttijd en de wasbeurt zelf. Voor één enkele vrachtwagen lijkt dat overzienbaar. Maar wanneer u een vloot van 20, 50 of 100+ voertuigen onderhoudt, lopen die uren snel op tot tientallen verloren chauffeursuren per maand.
        </p>
        <p>
          Met onze <strong className="text-white">truckwash op locatie</strong> komt de service naar het voertuig in plaats van andersom. De vrachtwagen blijft staan waar hij toch al staat: op het bedrijfsterrein, bij het distributiecentrum, of op een afgesproken laadplaats. Voor wagenparkbeheerders betekent dat een directe besparing op rijtijd, brandstof en het ATP/SLA-risico van te late afleveringen.
        </p>
        <p>
          Bij <Link to="/fleet-cleaning" className="text-[#00D4FF] hover:underline">Fleet Cleaning-contracten</Link> vanaf 10 voertuigen leggen wij een vaste cyclus en vaste prijzen vast. Wekelijks, tweewekelijks of maandelijks — afhankelijk van uw inzet en sector.
        </p>
      </Section>

      {/* === FLEXIBILITY === */}
      <Section
        id="flexibiliteit"
        overline="Flexibele inzet"
        title="Eén service, vele scenario's."
      >
        <p>
          Niet elke vloot is hetzelfde. Een foodservice-distributeur heeft andere wasbehoeften dan een bouwlogistiek bedrijf of een transporteur in de chemielogistiek. Daarom werkt Wasz.nl met aanpasbare reinigingsprotocollen. Zo combineren we standaard exterieurreiniging met aanvullende ontvettingsstappen voor zware verontreiniging, een HACCP-conforme procedure voor voedseltransport, of een snelle wash-and-go voor distributievoertuigen.
        </p>
        <p>
          Onze service is geschikt voor <strong className="text-white">wagenpark reiniging</strong> in alle gangbare scenario's:
        </p>
        <ul className="space-y-2.5">
          {[
            "Containertransport en bulkvervoer (havens, intermodaal)",
            "Foodservice, retail-distributie en koeltransport",
            "Bouwlogistiek en betontransport",
            "E-commerce en pakkettransport",
            "Stadsdistributie binnen milieuzones",
            "Bedrijfsbussen en serviceauto's",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-[#00D4FF] shrink-0 mt-0.5" strokeWidth={1.8} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* === SUSTAINABILITY === */}
      <Section
        id="duurzaam"
        overline="Milieubewust"
        title="Mobiele vrachtwagenreiniging hoeft niet vervuilend te zijn."
      >
        <p>
          Een schone vloot en een schoon milieu sluiten elkaar niet uit. Wasz.nl werkt standaard met biologisch afbreekbare reinigingsmiddelen die door alle gangbare waterzuiveringsinstallaties verwerkt kunnen worden. Het waterverbruik per voertuig houden we zo laag mogelijk; veel lager dan een traditionele wasstraat die met hergebruikbassins werkt.
        </p>
        <p>
          Voor het afvoeren van vuilwater werken wij volgens de Nederlandse milieuregels. Op locaties zonder geschikte afvoer brengen wij eigen voorzieningen mee om wegloop in het oppervlaktewater te voorkomen.
        </p>
        <p>
          De combinatie van <strong className="text-white">mobiele truckwash op locatie</strong> en gecontroleerde middelinzet maakt onze service ook geschikt voor opdrachtgevers die in hun eigen rapportage (CSRD, MVO-jaarverslag) verantwoording moeten afleggen over hun keten.
        </p>
      </Section>

      {/* === WASZBASE INTEGRATIE === */}
      <section id="waszbase" className="scroll-mt-28 py-16 sm:py-20 bg-[#0a0a18]/40 border-y border-white/5">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="section-overline mb-3">WaszBase</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-5">
                Elke wasbeurt direct digitaal vastgelegd.
              </h2>
            </div>
            <div className="lg:col-span-7 text-white/70 text-base sm:text-[17px] leading-relaxed space-y-4">
              <p>
                Mobiele truckwash levert pas écht waarde op als de bewijsvoering meekomt. <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link> is het klantportaal van Wasz.nl waarin elke wasbeurt automatisch wordt geregistreerd per kenteken: type wasbeurt, datum, locatie, operator en reinigingscertificaat.
              </p>
              <p>
                Voor wagenparkbeheerders betekent dat: één plek voor alle reinigingsregistraties, factuurhistorie per wasdag met onderliggende details per voertuig en kostenplaats, en directe export naar PDF of CSV voor de boekhouding. Bij NVWA-controles of audits levert u binnen seconden de relevante documenten aan.
              </p>
              <p>
                WaszBase is inbegrepen bij onze contractdiensten — geen aparte licentie, geen koppelingsproject. U logt in met uw eigen omgeving via <Link to="/waszbase" className="text-[#00D4FF] hover:underline">waszbase.co.nl/klant/login</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* === NATIONAL COVERAGE === */}
      <section id="dekking" className="scroll-mt-28 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <p className="section-overline mb-3">Landelijke inzetbaarheid</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-6">
            Mobiele truckwash door heel Nederland.
          </h2>
          <p className="text-white/70 text-base sm:text-[17px] leading-relaxed max-w-3xl mb-10">
            Vanuit Almere bedienen wij vijf kernregio's met dezelfde teams, dezelfde protocollen en dezelfde digitale rapportage. Voor specifieke industrieterreinen, havengebieden of grote distributiecentra hebben we vaak al een gevalideerd protocol klaar.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {regions.map(({ city, slug, note }) => (
              <Link
                key={slug}
                to={`/truckwash-${slug}`}
                data-testid={`region-link-${slug}`}
                className="group rounded-xl border border-white/10 bg-[#0F0F23] p-5 hover:border-[#00D4FF]/40 hover:bg-[#0F0F23]/80 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
                  <span className="text-base font-semibold text-white">{city}</span>
                </div>
                <p className="text-xs text-white/45 leading-relaxed">{note}</p>
                <p className="text-[11px] uppercase tracking-wider text-[#00D4FF]/0 group-hover:text-[#00D4FF] transition-colors mt-3 font-semibold">
                  Bekijk regio →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section id="hoe-werkt" className="scroll-mt-28 py-16 sm:py-20 bg-[#0a0a18]/40 border-y border-white/5">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <p className="section-overline mb-3">Stap voor stap</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-10">
            Hoe een mobiele wasbeurt verloopt.
          </h2>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                step: "01",
                title: "Aanvraag & intake",
                body: "U deelt uw wagenpark-omvang, locatie en gewenste cyclus. Binnen één werkdag krijgt u een gerichte offerte.",
              },
              {
                step: "02",
                title: "Planning op afspraak",
                body: "We plannen vaste tijdvensters in uw operationele ritme — vaak buiten de piekuren of in dalmomenten van de planning.",
              },
              {
                step: "03",
                title: "Reiniging op locatie",
                body: "Onze service-units komen naar uw terrein. Per voertuig reinigen wij volgens het overeengekomen protocol — exterieur, ontvetting en HACCP-stappen waar nodig.",
              },
              {
                step: "04",
                title: "Digitaal certificaat",
                body: "Elke wasbeurt verschijnt direct in WaszBase. Reinigingscertificaten en facturen zijn beschikbaar voor uw vlootbeheer en NVWA-dossier.",
              },
            ].map((s) => (
              <li
                key={s.step}
                className="rounded-2xl border border-white/10 bg-[#0F0F23] p-6"
              >
                <div className="text-[#00D4FF] text-xs font-bold tracking-[0.2em] mb-3">{s.step}</div>
                <h3 className="text-base font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* === WAAROM TRANSPORTBEDRIJVEN KIEZEN VOOR WASZ.NL === */}
      <Section
        id="waarom-wasz"
        overline="Waarom Wasz.nl"
        title="Waarom transportbedrijven kiezen voor Wasz.nl."
      >
        <p>
          Transportbedrijven en fleetmanagers kiezen voor <strong className="text-white">Wasz.nl</strong> omdat onze mobiele truckwash naadloos past in hun operationele realiteit. Sinds 2016 werken wij vanuit Almere voor wagenparken in heel Nederland — van foodservice-vloten in Amsterdam en versdistributeurs in het Westland tot containertrekkers op de Maasvlakte en e-commerce vloten op Lage Weide. De rode draad in onze klantenkring: ondernemers die operationele rust hoger waarderen dan de laagste stuksprijs en die willen dat hun waspartner met hen meedenkt over planning, compliance en administratie.
        </p>
        <p>
          Wij onderscheiden ons in vier dimensies. <strong className="text-white">Ten eerste op-locatie:</strong> uw vrachtwagen blijft staan waar hij toch al staat — geen rijtijd, geen wachttijd, geen verloren chauffeursuren. <strong className="text-white">Ten tweede transparant:</strong> één planner als vast aanspreekpunt, transparante prijzen per wasbeurt of contractcyclus, geen verborgen toeslagen. <strong className="text-white">Ten derde digitaal:</strong> elke wasbeurt automatisch in <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link>, met per-kenteken dossier, exporteerbare documenten en realtime status. <strong className="text-white">Ten vierde schaalbaar:</strong> van wagenparken vanaf 10 voertuigen tot <Link to="/wagenpark-reiniging" className="text-[#00D4FF] hover:underline">fleet cleaning</Link> contracten voor vloten van 200+ over meerdere locaties.
        </p>
        <p>
          Voor fleetmanagers die hun werk al druk hebben met planning, onderhoud en personeelsbeleid, is een waspartner die zonder extra zorgen door de keten loopt geen luxe maar een operationele voorwaarde. Onze klanten lopen doorgaans op stilzwijgende verlenging van hun contract — niet door juridische dwang, maar door geleverde kwaliteit. Bekijk ook onze <Link to="/blog/fleet-cleaning-contract-checklist" className="text-[#00D4FF] hover:underline">fleet cleaning contract checklist</Link> of de <Link to="/van-demo-tot-digitale-wagenparkcontrole" className="text-[#00D4FF] hover:underline">praktijkcase 'Van demo tot digitale wagenparkcontrole'</Link> voor een concrete kijk in onze werkwijze.
        </p>
      </Section>

      {/* === DIGITALE REGISTRATIE VIA WASZBASE === */}
      <Section
        id="digitale-registratie"
        overline="Digitale registratie"
        title="Digitale registratie via WaszBase."
      >
        <p>
          Iedere wasbeurt die wij uitvoeren, wordt direct en automatisch geregistreerd in <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link> — ons digitale klantportaal voor wagenparkadministratie. Per kenteken houden wij bij wanneer en waar een voertuig is gereinigd, welke werkwijze is toegepast (exterieur, chassis, regulier interieur of HACCP-werkwijze), welke operator de wasbeurt heeft uitgevoerd en — voor HACCP — welke middelen zijn gebruikt, met contacttijd en droogtijd. Geen papieren bonnen, geen handmatige Excel-overzichten, geen losse e-mails: één centraal dossier dat 24/7 beschikbaar is.
        </p>
        <p>
          Voor uw <strong className="text-white">wagenparkbeheerder</strong> betekent dat realtime inzicht: welke voertuigen zijn vrijgegeven voor de volgende rit, welke staan nog op de wasvloer en welke moeten in de volgende cyclus mee. Voor uw <strong className="text-white">kwaliteitscoördinator</strong> is WaszBase de centrale plek waar HACCP-certificaten, reinigingsregistraties en audit-exports met enkele klikken beschikbaar zijn. Voor uw <strong className="text-white">boekhouding</strong> betekent het één maandfactuur met onderliggende PDF-uitsplitsing per kenteken of per vestiging — geen reconciliatiewerk meer met losse wasbonnen.
        </p>
        <p>
          De volledige functionaliteit voor <Link to="/digitale-wagenparkadministratie" className="text-[#00D4FF] hover:underline">digitale wagenparkadministratie</Link>, <Link to="/reinigingsregistratie-transport" className="text-[#00D4FF] hover:underline">reinigingsregistratie transport</Link> en <Link to="/certificaatbeheer-wagenpark" className="text-[#00D4FF] hover:underline">certificaatbeheer voor uw wagenpark</Link> is standaard inbegrepen — geen aparte licentie, geen IT-implementatie en geen koppelingsproject. U logt in via uw webbrowser. Voor multi-site wagenparken stellen wij gebruikersrollen in zodat fleetmanagers, kwaliteitscoördinatoren en boekhouding elk hun eigen toegangsniveau hebben binnen één centraal dossier.
        </p>
      </Section>

      {/* === HACCP-CERTIFICATEN EN AUDITVOORBEREIDING === */}
      <Section
        id="haccp-audit"
        overline="HACCP & audit"
        title="HACCP-certificaten en auditvoorbereiding."
      >
        <p>
          Voor levensmiddelentransport, foodservice-vloten en versdistributeurs zijn <Link to="/haccp-certificaten" className="text-[#00D4FF] hover:underline">HACCP-certificaten</Link> geen luxe maar een operationele noodzaak. Wij voeren HACCP-conforme interieurreiniging uit voor laadruimtes van foodtransport en leveren per wasbeurt een digitaal certificaat in WaszBase. Het certificaat bevat alle elementen die de NVWA verwacht bij controle: kenteken, datum, locatie, type werkwijze, gebruikte reinigings- en desinfectiemiddelen (met productnaam en concentratie), contacttijd, droogtijd en operator-handtekening. Het is binnen vier uur na de wasbeurt downloadbaar als PDF.
        </p>
        <p>
          Voor <strong className="text-white">leveranciersaudits</strong> door grote afnemers — supermarktketens, foodservice-distributeurs, internationale producenten — biedt het centrale dossier in WaszBase een groot voordeel. Waar voorheen een audit een halve werkdag stress betekende met losse reinigingsbonnen verzamelen, sorteren en in een dossier ordenen, kan uw kwaliteitscoördinator nu binnen 30 minuten een complete PDF-export aanleveren. Wij zien dat klanten die op WaszBase overschakelden gemiddeld 60 tot 80 procent minder tijd kwijt zijn aan de voorbereiding van een audit.
        </p>
        <p>
          Voor klanten die regelmatig audits ondergaan, denken wij proactief mee in de voorbereiding: een periodieke review van uw dossier, een check of alle cycli volgens afspraak lopen en welke documenten extra nuttig zijn voor de eerstvolgende audit. Lees ook onze <Link to="/blog/haccp-vrachtwagenreiniging-gids" className="text-[#00D4FF] hover:underline">HACCP-gids voor vrachtwagenreiniging</Link> en de <Link to="/blog/nvwa-controle-voorbereiden" className="text-[#00D4FF] hover:underline">kennisbank-gids over NVWA-controle voorbereiden</Link> voor de praktische details. Voor een uitgebreid praktijkverhaal: de <Link to="/van-demo-tot-digitale-wagenparkcontrole" className="text-[#00D4FF] hover:underline">case &lsquo;Van demo tot digitale wagenparkcontrole&rsquo;</Link>.
        </p>
      </Section>

      {/* === FAQ === */}
      <section id="faq" className="scroll-mt-28 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <p className="section-overline mb-3">FAQ</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1] mb-10">
            Veelgestelde vragen over mobiele truckwash.
          </h2>
          <div className="space-y-3">
            {PAGE_FAQS.map((f, i) => (
              <details
                key={i}
                data-testid={`seo-faq-${i}`}
                className="group rounded-xl border border-white/10 bg-[#0F0F23] p-5 sm:p-6 hover:border-[#00D4FF]/30 transition-colors"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <h3 className="text-base sm:text-lg font-semibold text-white pr-4">
                    {f.q_nl}
                  </h3>
                  <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform shrink-0" strokeWidth={1.8} />
                </summary>
                <p className="mt-4 text-sm sm:text-[15px] text-white/65 leading-relaxed">{f.a_nl}</p>
              </details>
            ))}
          </div>
          <p className="mt-8 text-sm text-white/45">
            Meer vragen?{" "}
            <Link to="/faq" className="text-[#00D4FF] hover:underline">Bekijk alle veelgestelde vragen</Link>{" "}
            of <Link to="/contact?type=quote" className="text-[#00D4FF] hover:underline">neem direct contact op</Link>.
          </p>
        </div>
      </section>

      {/* === CTA STRIP === */}
      <section className="scroll-mt-28 py-12 border-y border-white/5 bg-gradient-to-r from-[#0a0a18] via-[#0F0F23] to-[#0a0a18]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Plan een reiniging op locatie.</h2>
                <p className="text-sm text-white/55 mt-1">
                  Vertel ons uw vlootomvang en regio — we reageren binnen één werkdag.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact?type=quote" data-testid="seo-cta-strip-quote">
                <button className="btn-primary">
                  Vraag een offerte aan <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/contact" data-testid="seo-cta-strip-contact">
                <button className="btn-secondary">Neem contact op</button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* === INTERNAL LINKS BLOCK (SEO) === */}
      <section className="py-14">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <p className="section-overline mb-3">Meer over onze diensten</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/vrachtwagenreiniging", title: "Vrachtwagenreiniging", desc: "Reiniging per voertuig op afspraak." },
              { to: "/fleet-cleaning", title: "Fleet Cleaning", desc: "Vaste contracten voor vloten vanaf 10 voertuigen." },
              { to: "/wagenpark-reiniging", title: "Wagenparkreiniging", desc: "Structureel onderhoud voor bedrijfsvloten." },
              { to: "/haccp-certificaten", title: "HACCP & Certificaten", desc: "Compliant reiniging met bewijsvoering." },
              { to: "/waszbase", title: "WaszBase klantportaal", desc: "Digitaal dossier per kenteken, 24/7." },
              { to: "/digitale-wagenparkadministratie", title: "Digitale wagenparkadministratie", desc: "Kentekendossier, registratie en facturatie." },
              { to: "/reinigingsregistratie-transport", title: "Reinigingsregistratie transport", desc: "Digitale bewijslast per kenteken, NVWA-klaar." },
              { to: "/certificaatbeheer-wagenpark", title: "Certificaatbeheer wagenpark", desc: "HACCP- en reinigingsdocumenten centraal." },
            ].map(({ to, title, desc }) => (
              <Link
                key={to}
                to={to}
                className="group rounded-xl border border-white/10 bg-[#0F0F23] p-5 hover:border-[#00D4FF]/40 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Building2 className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
                  <span className="text-sm font-semibold text-white">{title}</span>
                </div>
                <p className="text-xs text-white/55 leading-relaxed mb-3">{desc}</p>
                <span className="text-[11px] uppercase tracking-wider text-white/40 group-hover:text-[#00D4FF] transition-colors font-semibold">
                  Bekijk →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default MobieleTruckwashSEO;
